import Stack from "@mui/material/Stack/Stack";
import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  TextField,
  Backdrop,
  CircularProgress,
  Typography,
  Fab,
  Input,
} from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { Container } from "@mui/system";
import DrawerAppBar from "../../../../components/mobile/Navbar";
import { useNavigate } from "react-router-dom";
import { LoadProductList } from "../../../../../domain/usecases/load-product-list";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { useLocation } from "react-router-dom";
import { Product } from "../../../../../domain/models/product-model";
import { CustomDialog } from "../../../../components/Dialog";
import { CreateBreakageReport } from "../../../../../domain/usecases/create-breakage-report";

type Props = {
  loadProductList: LoadProductList;
  createBreakageReport: CreateBreakageReport;
};

export const BreakageProductCollector: React.FC<Props> = ({
  loadProductList,
  createBreakageReport,
}) => {
  const [code, setCode] = useState<string>("");
  const [batch, setBatch] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleConfirm = async () => {
    try {
      if (!code) return alert("Preencha o código antes de continuar");
      if (product?.batchControl && !batch)
        return alert("Preencha o lote antes de continuar");
      setIsLoading(true);
      await createBreakageReport.create({
        positionId: location.state.position.id!,
        productCode: code,
        batch,
        amount: Number(
          (Number(amount) * (product?.packageAmount || 1)).toFixed(2)
        ),
      });
      navigate("/mobile/tasks");
    } catch (error: any) {
      console.log(error);
      alert(error.message || "Ocorreu um erro. Tente novamente em breve.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputBlur = async () => {
    setIsLoading(true);
    try {
      if (code) {
        if (product && product.eanCode === code) return;
        const data = await loadProductList.load({ code });
        setProduct(data[0]);
      }
    } catch (error) {
      setIsErrorDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        isOpen={isErrorDialogOpen}
        title="Erro ao carregar produto"
        content={`Não foi encontrado nenhum produto com o código EAN ${code}`}
        handleClose={() => {
          setIsErrorDialogOpen(false);
        }}
      />
      <CustomDialog
        isOpen={isDialogOpen}
        title="Confirmar quebra"
        content="Deseja confirmar a quebra do produto?"
        handleClose={() => {
          setIsDialogOpen(false);
        }}
        onYes={handleConfirm}
        onNo={() => {
          setIsDialogOpen(false);
        }}
        yesText="Confirmar"
        noText="Cancelar"
        isLoading={isLoading}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DrawerAppBar
        title="Coletar dados do produto"
        onBack={() => {
          navigate("/mobile/breakage");
        }}
      />
      <Stack sx={{ m: "20px" }} spacing={4}>
        <Paper>
          <Stack spacing={4} sx={{ mx: "10px", my: "30px" }}>
            <Stack direction="row">
              <TextField
                id="outlined-basic"
                label="Código do Produto"
                variant="outlined"
                sx={{
                  mx: "5px",
                }}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                onBlur={handleInputBlur}
              />
              <Button
                variant="outlined"
                color="inherit"
                sx={{
                  mx: "5px",
                }}
              >
                <QrCodeIcon />
              </Button>
            </Stack>
            {product?.batchControl ? (
              <Container>
                <TextField
                  id="outlined-basic"
                  label="Lote"
                  variant="outlined"
                  sx={{
                    mx: "5px",
                  }}
                  value={batch}
                  onChange={(e) => {
                    setBatch(e.target.value);
                  }}
                />
              </Container>
            ) : (
              ""
            )}
            <Stack>
              <Typography sx={{ mx: "5px" }}>Quantidade</Typography>
              <Stack
                direction="row"
                sx={{
                  my: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={() => {
                    if (amount && amount !== "0") {
                      const newValue = parseFloat(amount) - 1;
                      setAmount(newValue.toString());
                    }
                  }}
                >
                  <RemoveIcon />
                </Fab>

                <Input
                  sx={{ width: "70px", mx: "30px" }}
                  type="text"
                  value={amount}
                  onChange={(event) => {
                    const inputValue = event.target.value;
                    if (/^[0-9.]+$/.test(inputValue) || inputValue === "") {
                      setAmount(inputValue);
                    }
                  }}
                />
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={() => {
                    if (amount) {
                      const newValue = parseFloat(amount) + 1;
                      setAmount(newValue.toString());
                    } else {
                      setAmount("1");
                    }
                  }}
                >
                  <AddIcon />
                </Fab>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        {product && (
          <Paper sx={{ padding: "20px" }}>
            <Stack spacing={2}>
              <Typography fontWeight="bold" fontSize="24px">
                {product.description}
              </Typography>
              <Typography>{`Fornecedor: ${product.supplier}`}</Typography>
              {batch && <Typography>{`Lote: ${batch}`}</Typography>}
              <Typography>{`Quantidade: ${amount}`}</Typography>
            </Stack>
          </Paper>
        )}

        <Button
          variant="contained"
          color="secondary"
          sx={{ color: "white", height: "50px", fontWeight: "bold" }}
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          Continuar
        </Button>
      </Stack>
    </>
  );
};
